import { mfgXsInputClass, mfgXsPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
// Upload-receipt form (slice 4e). Extracted from
// `ManufacturingInventoryReceiptsBody` to keep that file under the
// 400-line hard threshold. Stateless presentational component; the
// parent owns the draft + file refs + submit handler.
//
// Plant picker is omitted intentionally -- the server resolves to the
// org's default plant when `plant_id` is missing. A picker can be
// wired in once the Inventory panel grows a shared plant scope.

export type ReceiptUploadDraft = {
  vendor: string;
  date: string;
  total: string;
  shipping: string;
  tax: string;
  notes: string;
};

type Props = {
  draft: ReceiptUploadDraft;
  setDraft: (d: ReceiptUploadDraft) => void;
  file: File | null;
  setFile: (f: File | null) => void;
  working: boolean;
  onSubmit: () => void;
};

export function ManufacturingReceiptUploadForm({
  draft,
  setDraft,
  file,
  setFile,
  working,
  onSubmit,
}: Props) {
  function patch(p: Partial<ReceiptUploadDraft>) {
    setDraft({ ...draft, ...p });
  }
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2 dark:border-zinc-700 dark:bg-zinc-800/40">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-600 dark:text-zinc-300">
        Upload receipt
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="flex flex-col gap-0.5 text-[11px] text-gray-600 dark:text-zinc-400">
          Vendor
          <input
            className={mfgXsInputClass}
            value={draft.vendor}
            onChange={(e) => patch({ vendor: e.target.value })}
            placeholder="(optional)"
            disabled={working}
          />
        </label>
        <label className="flex flex-col gap-0.5 text-[11px] text-gray-600 dark:text-zinc-400">
          Receipt date
          <input
            type="date"
            className={mfgXsInputClass}
            value={draft.date}
            onChange={(e) => patch({ date: e.target.value })}
            disabled={working}
          />
        </label>
        <label className="flex flex-col gap-0.5 text-[11px] text-gray-600 dark:text-zinc-400">
          Total
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            className={mfgXsInputClass}
            value={draft.total}
            onChange={(e) => patch({ total: e.target.value })}
            placeholder="0.00"
            disabled={working}
          />
        </label>
        <label className="flex flex-col gap-0.5 text-[11px] text-gray-600 dark:text-zinc-400">
          Shipping
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            className={mfgXsInputClass}
            value={draft.shipping}
            onChange={(e) => patch({ shipping: e.target.value })}
            placeholder="0.00"
            disabled={working}
          />
        </label>
        <label className="flex flex-col gap-0.5 text-[11px] text-gray-600 dark:text-zinc-400">
          Tax
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            className={mfgXsInputClass}
            value={draft.tax}
            onChange={(e) => patch({ tax: e.target.value })}
            placeholder="0.00"
            disabled={working}
          />
        </label>
        <label className="flex flex-col gap-0.5 text-[11px] text-gray-600 dark:text-zinc-400">
          File (PDF or image)
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            className={`${mfgXsInputClass} file:mr-2 file:rounded file:border-0 file:bg-indigo-50 file:px-2 file:py-0.5 file:text-[11px] file:font-semibold file:text-indigo-700 dark:file:bg-indigo-900/40 dark:file:text-indigo-200`}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={working}
          />
        </label>
      </div>
      <label className="flex flex-col gap-0.5 text-[11px] text-gray-600 dark:text-zinc-400">
        Notes
        <textarea
          rows={2}
          className={mfgXsInputClass}
          value={draft.notes}
          onChange={(e) => patch({ notes: e.target.value })}
          placeholder="(optional)"
          disabled={working}
        />
      </label>
      <div className="flex items-center justify-end gap-2">
        <span className="text-[11px] text-gray-500 dark:text-zinc-500">
          {file ? `${file.name} · ${(file.size / 1024).toFixed(0)} KB` : "No file picked"}
        </span>
        <button
          type="button"
          className={mfgXsPrimaryBtnClass}
          onClick={onSubmit}
          disabled={working || !file}
        >
          {working ? "Uploading…" : "Upload receipt"}
        </button>
      </div>
    </div>
  );
}
